import type { ComputedRef } from 'vue'
import type { CustomerBrand } from '@shared/config'

import { useKidStore } from './store'
import { useKidDetailsApi } from '../api/details.hook'
import { useKidCustomerGoApi } from '../api/customer-go.hook'
import { useKidUpdateApi } from '../api/update.hook'
import { useKidPrivileges } from './kid-privileges.hook'

interface SectionKidGeneralReturn {
  refTitle: ComputedRef<string>
  refBrand: ComputedRef<CustomerBrand | null>
  isCustomerGo: ComputedRef<boolean>
  hasKid: ComputedRef<boolean>
  isProcessing: ComputedRef<boolean>
  canViewKids: ComputedRef<boolean>
  canManageKids: ComputedRef<boolean>
  canDeleteKids: ComputedRef<boolean>
  save: () => void
}

export const useSectionKidGeneral = (): SectionKidGeneralReturn => {
  const kidStore = useKidStore()
  const { canViewKids, canManageKids, canDeleteKids, canViewCustomers } = useKidPrivileges()

  const refTitle = computed<string>(() => `Kid / ${kidStore.getKid?.name ?? ''}`)
  const refBrand = computed<CustomerBrand | null>(() => kidStore.getKid?.customer.brand ?? null)
  const isCustomerGo = computed<boolean>(() => kidStore.isCustomerGo)
  const hasKid = computed<boolean>(() => kidStore.hasKid)
  const isProcessing = computed<boolean>(() => kidStore.isProcessing)

  const submit = async (): Promise<void> => {
    const kid = kidStore.getKid
    if (!kid) return

    const isUpdated = await useKidUpdateApi({
      kidId: kid.id,
      payload: { ...kidStore.getForm },
    })

    if (isUpdated) {
      await useKidDetailsApi({ kidId: kid.id })
    }
  }

  const save = (): void => {
    void submit()
  }

  watch(() => kidStore.getCustomerId, (customerId) => {
    if (customerId && canViewCustomers.value) {
      void useKidCustomerGoApi({ customerId })
    }
  }, { immediate: true })

  return {
    refTitle,
    refBrand,
    isCustomerGo,
    hasKid,
    isProcessing,
    canViewKids,
    canManageKids,
    canDeleteKids,
    save,
  }
}
